import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';

/**
 * Agent drill-down dropdown for broker/super_admin views. Hidden for agents.
 *
 * The parent owns the selected agent_id state — this component just renders
 * a controlled <select> and calls `onChange(agentId|null)` when the broker picks one.
 *
 * Usage:
 *   const [agentId, setAgentId] = useState(null);
 *   <AgentFilter value={agentId} onChange={setAgentId} />
 *
 * Pass `value={null}` for "All Agents" (brokerage-wide).
 */
export default function AgentFilter({ value, onChange, label = 'Agent', className = '' }) {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const canFilter = user && ['broker', 'super_admin'].includes(user.role);

  useEffect(() => {
    if (!canFilter) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/api/agents/brokerage`);
        if (res.ok && !cancelled) {
          const list = await res.json();
          setAgents(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        // Silent — the dropdown just won't appear, which fails safely.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [canFilter]);

  if (!canFilter) return null;
  if (!loaded || agents.length === 0) return null;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <label className="text-gray-400 text-xs uppercase tracking-wider">{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? null : Number(v));
        }}
        className="bg-[#0A0E27] border border-[#546E7A]/50 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#00D4FF]"
      >
        <option value="">All Agents</option>
        {agents.map((a) => (
          <option key={a.id} value={a.id}>{a.name}</option>
        ))}
      </select>
    </div>
  );
}
